import React, { useEffect, useRef, useState } from 'react';
import Modal from 'react-modal';
import { useFocus, useOverlay } from '../../contexts/FocusContext';
import { getFullscreenPortalTarget } from './fullscreenPortal';

export interface WorldScreenFeed {
  video: HTMLVideoElement;
  isActive: () => boolean;
}

/** Display only: ownership of the stream, its tracks and audio stays with Unity/IVS. */
export default function WorldScreenFocus() {
  const [feed, setFeed] = useState<WorldScreenFeed | null>(null);
  const { hasActiveOverlay } = useFocus();
  const blocked = useRef(hasActiveOverlay); blocked.current = hasActiveOverlay;
  const video = useRef<HTMLVideoElement>(null);
  const [playBlocked, setPlayBlocked] = useState(false);
  useOverlay('world-screen-focus', !!feed);

  useEffect(() => {
    const open = (event: Event) => {
      const candidate = (event as CustomEvent<WorldScreenFeed>).detail;
      if (blocked.current || !(candidate?.video instanceof HTMLVideoElement) || typeof candidate.isActive !== 'function') return;
      if (!candidate.isActive() || !(candidate.video.srcObject instanceof MediaStream)) return;
      setFeed(candidate);
    };
    window.addEventListener('WorldScreenFocus', open);
    return () => window.removeEventListener('WorldScreenFocus', open);
  }, []);

  useEffect(() => {
    if (!feed) return;
    let disposed = false;
    let attached: MediaStream | null = null;
    let attachedElement: HTMLVideoElement | null = null;
    const sync = () => {
      const stream = feed.video.srcObject as MediaStream | null;
      // Leaving the room, losing access, or ending the share must dismiss focus too.
      if (!feed.isActive() || !stream?.getVideoTracks().some(track => track.readyState === 'live')) {
        setFeed(null); return;
      }
      const element = video.current;
      if (!element || attached === stream) return;
      attached = stream; attachedElement = element; element.srcObject = stream; setPlayBlocked(false);
      void element.play().catch(() => { if (!disposed) setPlayBlocked(true); });
    };
    sync();
    const timer = window.setInterval(sync, 250);
    return () => {
      disposed = true; window.clearInterval(timer);
      // Never stop shared tracks, pause the source video, leave IVS, or unpublish.
      if (attachedElement) attachedElement.srcObject = null;
    };
  }, [feed]);

  return <Modal isOpen={!!feed} onRequestClose={() => setFeed(null)} contentLabel="Expanded room screenshare"
    parentSelector={getFullscreenPortalTarget as () => HTMLElement}
    style={{
      overlay: { backgroundColor: '#080e16', zIndex: 2147483000 },
      content: { inset: 0, border: 0, borderRadius: 0, padding: 0, background: '#080e16', color: '#fff', display: 'flex', flexDirection: 'column' },
    }}>
    <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '12px 20px' }}>
      <button autoFocus onClick={() => setFeed(null)} style={{ padding: '10px 16px', border: '1px solid #8da4ba', borderRadius: 8 }}>Close expanded view ✕</button>
    </header>
    <video ref={video} autoPlay playsInline muted aria-label="Expanded screenshare video" style={{ width: '100%', flex: 1, minHeight: 0, objectFit: 'contain' }} />
    {playBlocked && <button onClick={() => { void video.current?.play().then(() => setPlayBlocked(false)).catch(() => {}); }} style={{ padding: 16 }}>Play screenshare</button>}
  </Modal>;
}
